import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import BackrsFilledDropShadow from '@backrs_ui/common-ui/src/assets/images/backrs-filled-drop-shadow.svg';
import { EmulationChip } from '@backrs_ui/common-ui/src/atoms/EmulationChip';
import { AppIcon, AppIcons, ICON_SIZES } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';

import { SxProps, Theme, Box, Typography } from '@mui/material';

import { SIDEBAR_MOBILE_HEIGHT } from './sidebar.constants';

const SIDEBAR_STRINGS = appStrings.modules.backrsSidebar;

export type MobileNavItem = Readonly<{
  iconName: AppIcons;
  onClick: () => void;
}>;

type SidebarMobileTopBarProps = Readonly<{
  onMenuClick: () => void;
  onLogoClick: () => void;
  isEmulating: boolean;
  mobileNavItems?: MobileNavItem[];
}>;

export const SidebarMobileTopBar = ({
  onMenuClick,
  onLogoClick,
  isEmulating,
  mobileNavItems,
}: SidebarMobileTopBarProps) => {
  return (
    <Box sx={containerSx(isEmulating)}>
      <Box sx={logoContainerSx}>
        <Box
          component="img"
          src={BackrsFilledDropShadow}
          alt="Backrs Logo"
          sx={backrsLogoImageSx}
          onClick={onLogoClick}
        />
        <Box sx={logoTextContainerSx}>
          <Box sx={logoTextWithChipSx}>
            <Typography variant="display-xs-semibold" sx={logoTextSx}>
              {SIDEBAR_STRINGS.backrs}
            </Typography>
            {isEmulating && <EmulationChip />}
          </Box>

          <Box sx={mobileNavItemWrapperSx}>
            {mobileNavItems?.map((item, index) => (
              <Box key={index} onClick={item.onClick} sx={menuIconSx}>
                <AppIcon iconName={item.iconName} diameter="24px" color="currentColor" />
              </Box>
            ))}
            <Box onClick={onMenuClick} sx={menuIconSx}>
              <AppIcon iconName="Menu02" diameter="24px" color="currentColor" />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const containerSx =
  (isEmulating: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: { xs: 'flex', sm: 'none' },
    flexDirection: 'column',
    justifyContent: 'space-between',
    boxSizing: 'border-box',
    width: '100vw',
    height: SIDEBAR_MOBILE_HEIGHT,
    backgroundColor: backrs.colors['Colors/Background/bg-primary'],
    py: backrs.spacing['spacing-lg'],
    pr: backrs.spacing['spacing-md'],
    pl: backrs.containerSpacing['container-padding-mobile'],
    borderBottom: isEmulating
      ? `2px solid ${backrs.colors['Colors/Foreground/fg-success-secondary']}`
      : `1px solid ${backrs.colors['Colors/Base/Black']}`,
    position: 'fixed',
    top: 0,
    zIndex: 1200,
  });

const logoContainerSx: SxProps<Theme> = ({ backrs }) => ({
  gap: backrs.spacing['spacing-lg'],
  display: 'flex',
  transition: 'all 0.3s ease-in-out',
});

const backrsLogoImageSx: SxProps<Theme> = {
  cursor: 'pointer',
  height: ICON_SIZES['32px'],
  width: ICON_SIZES['32px'],
};

const logoTextContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  gap: backrs.spacing['spacing-lg'],
});

const logoTextWithChipSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  minWidth: 0,
  overflow: 'hidden',
  gap: backrs.spacing['spacing-md'],
});

const logoTextSx: SxProps<Theme> = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const mobileNavItemWrapperSx: SxProps<Theme> = ({ backrs }) => ({
  gap: backrs.spacing['spacing-none'],
  display: 'flex',
  flexShrink: 0,
});

const menuIconSx: SxProps<Theme> = ({ backrs }) => ({
  cursor: 'pointer',
  borderRadius: backrs.radius['radius-full'],
  px: backrs.spacing['spacing-md'],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  alignSelf: 'center',
  color: backrs.colors['Colors/Foreground/fg-secondary'],
  transition: 'all 0.3s ease-in-out',
});
